import {InputDate} from '@/components/commons/input-date/InputDate'
import {WarningBanner} from '@/components/commons/warning-banner/WarningBanner'
import {Button} from '@/components/ui/button/Button'
import {DetailsField} from '@/components/ui/details-atoms/DetailsAtoms'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {CheckIcon} from '@/components/ui/icon/Icon'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Text} from '@/components/ui/text/Text'
import {
    StyledMarkAsTrasmittedDialogFooter,
    StyledMarkAsTrasmittedDialogBody
} from '@/features/projects/components/mark-as-transmitted-dialog/style'
import {useEditProjectDetails} from '@/features/projects/queries/useEditProjectDetails'
import {useBulkTransmission} from '@/features/projects/queries/useBulkTransmission'
import {ProjectStatus} from '@/features/projects/types'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {ComponentType, MouseEvent, useMemo, useState} from 'react'

export const MarkAsTrasmittedDialog = ({
    statuses,
    projectIds,
    hasOpeningExport,
    hasClosingExport,
    openingTransmissionAt,
    closingTransmissionAt,
    customTrigger: CustomTrigger
}: {
    statuses: ProjectStatus[]
    projectIds: string[]
    hasOpeningExport: boolean
    hasClosingExport: boolean
    openingTransmissionAt?: string | null
    closingTransmissionAt?: string | null
    customTrigger?: ComponentType<{onClick: (event?: MouseEvent<HTMLElement>) => void; isDisabled?: boolean}>
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    const isEnabled = useMemo(() => {
        return statuses.every(status => status != 'opening')
    }, [statuses])

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    CustomTrigger ? (
                        <CustomTrigger onClick={() => setIsOpen(true)} isDisabled={!isEnabled} />
                    ) : (
                        <Button variant="ghost" disabled={!isEnabled}>
                            <CheckIcon />
                            {t('projects:mark_siar_transmission')}
                        </Button>
                    )
                }
            />
            <Dialog width="380px" overflowY="unset">
                <MarkAsTrasmittedDialogContent
                    projectIds={projectIds}
                    hasOpeningExport={hasOpeningExport}
                    hasClosingExport={hasClosingExport}
                    openingTransmissionAt={openingTransmissionAt}
                    closingTransmissionAt={closingTransmissionAt}
                />
            </Dialog>
        </DialogProvider>
    )
}

const MarkAsTrasmittedDialogContent = ({
    projectIds,
    hasOpeningExport,
    hasClosingExport,
    openingTransmissionAt,
    closingTransmissionAt
}: {
    projectIds: string[]
    hasOpeningExport: boolean
    hasClosingExport: boolean
    openingTransmissionAt?: string | null
    closingTransmissionAt?: string | null
}) => {
    const {t} = useTranslation()
    const dialog = useDialogContext()

    const [openingDate, setOpeningDate] = useState<Date | null>(null)
    const [closingDate, setClosingDate] = useState<Date | null>(null)

    const isBulkTransmission = projectIds.length >= 2

    const updateProject = useEditProjectDetails({
        urlParams: {
            projectId: projectIds[0]
        },
        options: {
            onSuccess: () => {
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, projectIds[0]]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, projectIds[0]]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                toast.success(t('projects:siar_transmission_marked'))
                dialog?.setOpen(false)
            },
            onError: () => {
                toast.error(t('errors:default'))
            }
        }
    })

    const bulkOpeningTransmission = useBulkTransmission()
    const bulkClosingTransmission = useBulkTransmission()

    const handleConfirm = async () => {
        if (isBulkTransmission) {
            try {
                if (openingDate) {
                    await bulkOpeningTransmission.mutateAsync({
                        payload: {
                            projectIds,
                            type: 'opening',
                            transmissionDate: dayjs(openingDate).format('YYYY-MM-DD')
                        }
                    })
                }
                if (closingDate) {
                    await bulkClosingTransmission.mutateAsync({
                        payload: {
                            projectIds,
                            type: 'closing',
                            transmissionDate: dayjs(closingDate).format('YYYY-MM-DD')
                        }
                    })
                }
                toast.success(t('projects:siar_transmission_marked'))
                dialog?.setOpen(false)
            } catch {
                toast.error(t('errors:default'))
            }
        } else {
            updateProject.mutate({
                ...(openingDate && {openingTransmissionAt: dayjs(openingDate).format('YYYY-MM-DD')}),
                ...(closingDate && {
                    closingTransmissionAt: dayjs(closingDate).format('YYYY-MM-DD'),
                    progressStatus: 'transmitted'
                })
            })
        }
    }

    const isPending = isBulkTransmission
        ? bulkOpeningTransmission.isPending || bulkClosingTransmission.isPending
        : updateProject.isPending

    const hasAnyDate = (hasOpeningExport && openingDate != null) || (hasClosingExport && closingDate != null)

    const renderReadonlyDate = (label: string, value: string) => (
        <DetailsField fullWidth>
            <Text palette="neutral" shade="500" size="textSm">
                {label}
            </Text>
            <Text palette="neutral" shade="950" size="textSm" weight="500">
                {dayjs(value).format('DD/MM/YYYY')}
            </Text>
        </DetailsField>
    )

    return (
        <>
            <DialogHeader>
                <DialogHeading>{t('projects:mark_siar_transmission')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            <StyledMarkAsTrasmittedDialogBody>
                {openingTransmissionAt ? (
                    renderReadonlyDate(t('projects:opening_transmission_at'), openingTransmissionAt)
                ) : (
                    <InputDate
                        label={t('projects:opening_transmission_at')}
                        onChange={date => setOpeningDate(date ? dayjs(date).toDate() : null)}
                        placeholder="gg/mm/aaaa"
                        value={openingDate}
                        fullWidth
                        disabled={!hasOpeningExport}
                    />
                )}
                {closingTransmissionAt ? (
                    renderReadonlyDate(t('projects:closing_transmission_at'), closingTransmissionAt)
                ) : (
                    <InputDate
                        label={t('projects:closing_transmission_at')}
                        onChange={date => setClosingDate(date ? dayjs(date).toDate() : null)}
                        placeholder="gg/mm/aaaa"
                        value={closingDate}
                        fullWidth
                        disabled={!hasClosingExport}
                    />
                )}
                <WarningBanner
                    title={t('projects:siar_transmission_dates_warning')}
                    direction="row"
                    textSize="textSm"
                />
            </StyledMarkAsTrasmittedDialogBody>
            <StyledMarkAsTrasmittedDialogFooter>
                <Button variant="secondary" onClick={() => dialog?.setOpen(false)}>
                    {t('commons:close')}
                </Button>
                <Button variant="primaryNeutral" onClick={handleConfirm} disabled={!hasAnyDate || isPending}>
                    {t('commons:confirm')}
                    {isPending && <Spinner />}
                </Button>
            </StyledMarkAsTrasmittedDialogFooter>
        </>
    )
}
